import {
  ChangeUserProfile,
  InfoCard,
  LoadingScreen,
} from '@growdash/design-system/molecules';
import {
  CreditCardDto,
  PlanInformationStatusEnum,
  ProfileAccountSubscriptionResponse,
} from '@growdash/identity/generated';

import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { DollarSign } from 'lucide-react';
import { enqueueSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import { AddPaymentMethodDialog } from './components/add-payment-method-dialog';

import { Button, ConfirmDialog } from '@growdash/design-system/atoms';

import GrowIntelLogo from '../../assets/logo/logo-growintel.png';
import { ChangeBillingAddressDialog } from './components/change-billing-address-dialog';
import { InvoicesList } from './components/invoices-list';
import { ManagePaymentMethodsDialog } from './components/manage-payment-methods-dialog';

import { GrowIntelContext } from '../../context/context';
import {
  useAddCardMutation,
  useChangeBillingAddressMutation,
  useRemoveCardMutation,
  useSetDefaultCardMutation,
  useUpdateUserMutation,
} from '../../hooks/profile-mutations';
import { useGrowintel } from '../../hooks/useGrowIntel';

const stripePromise = loadStripe(import.meta.env.VITE_REACT_APP_STRIPE_KEY);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#32325d', // You might want to adjust colors based on your theme
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a', // Use your theme's error color
      iconColor: '#fa755a',
    },
  },
  hidePostalCode: true, // Optionally hide postal code field if not needed
};

const formatDate = (date: Date) => {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const ProfileViewContent = () => {
  const { lib } = useGrowintel();
  const { identityService } = lib;
  const context = useContext(GrowIntelContext);
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();
  const [isOpenChangeUserNameModal, setIsOpenChangeUserNameModal] =
    useState(false);

  const refreshUser = context?.config?.refreshUser;

  // Add effect to refetch when dependencies become available
  useEffect(() => {
    if (context?.user?.sub && identityService) {
      queryClient.invalidateQueries({ queryKey: ['profileSubscription'] });
      queryClient.invalidateQueries({ queryKey: ['creditCards'] });
    }
  }, [context?.user?.sub, identityService, queryClient]);

  const [stripeError, setStripeError] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [newCardName, setNewCardName] = useState('');
  const [isAddCardOpen, setIsAddCardOpen] = useState(false);
  const [isManageCardsOpen, setIsManageCardsOpen] = useState(false);
  const [isChangeAddressModalOpen, setIsChangeAddressModalOpen] =
    useState(false);

  // State for the new confirmation modal with timer
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmModalAction, setConfirmModalAction] = useState<
    (() => void) | null
  >(null);
  const [confirmModalTitle, setConfirmModalTitle] = useState('');
  const [confirmModalBaseDescription, setConfirmModalBaseDescription] =
    useState('');
  const [timer, setTimer] = useState(10);
  const [isConfirmButtonEnabled, setIsConfirmButtonEnabled] = useState(false);
  const [currentActionType, setCurrentActionType] = useState<
    'cancel' | 'reactivate' | null
  >(null);

  // React Query for fetching profile subscription info
  const {
    data: profileSubscriptionData,
    isLoading: isLoadingProfileSubscription,
    isError: isProfileSubscriptionError,
    error: profileSubscriptionErrorData,
  } = useQuery<ProfileAccountSubscriptionResponse, Error>({
    queryKey: ['profileSubscription', context?.user?.sub, identityService],
    queryFn: async () => {
      if (!identityService) {
        throw new Error(
          'Identity service is not available for profile subscription fetch',
        );
      }
      if (!context?.user?.sub) {
        throw new Error('User not available for profile subscription fetch');
      }
      const response =
        await identityService.profileAPI.profileControllerGetAccountSubscriptionInfo();
      refreshUser && refreshUser();
      if (!response?.data) {
        throw new Error('Failed to fetch profile subscription data.');
      }
      return response.data;
    },
    enabled: !!identityService,
  });

  if (isProfileSubscriptionError) {
    // Only show error if services are available (not during initial loading)
    if (context?.user?.sub && identityService) {
      enqueueSnackbar(
        `Failed to fetch profile subscription: ${
          profileSubscriptionErrorData?.message || 'Unknown error'
        }`,
        { variant: 'error' },
      );
      console.error(
        'Error fetching profile subscription:',
        profileSubscriptionErrorData,
      );
    }
  }

  const {
    data: cards = [],
    isLoading: isLoadingCards,
    isError: isCardsError,
    error: cardsErrorData,
  } = useQuery<CreditCardDto[], Error>({
    queryKey: ['creditCards'],
    queryFn: async () => {
      if (!identityService) {
        // This case should ideally be prevented by the 'enabled' option
        throw new Error(
          'Identity service is not available for fetching credit cards.',
        );
      }
      const response =
        await identityService.paymentsAPI.paymentsControllerGetCreditCards();
      return response?.data ?? []; // Ensure an array is always returned
    },
    enabled: !!(identityService && (context?.user?.products?.length ?? 0) > 0),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });

  if (isCardsError) {
    // Only show error if service is available (not during initial loading)
    if (identityService) {
      enqueueSnackbar(
        `Failed to fetch credit cards: ${
          cardsErrorData?.message || 'Unknown error'
        }`,
        { variant: 'error' },
      );
      console.error('Error fetching cards:', cardsErrorData);
    }
  }

  // Instantiate the new hooks
  const setDefaultCardMutation = useSetDefaultCardMutation({
    onSuccessCallback: () => {
      setIsManageCardsOpen(false);
      queryClient.invalidateQueries({ queryKey: ['profileSubscription'] });
      queryClient.invalidateQueries({ queryKey: ['creditCards'] });
    },
    identityService,
  });

  const removeCardMutation = useRemoveCardMutation({
    cards,
    identityService,
    // onSuccessCallback is not directly used here, but handled in ManagePaymentMethodsDialog
  });

  const addCardMutation = useAddCardMutation({
    stripe,
    elements,
    cardComplete,
    newCardName,
    cards,
    onSuccessCallback: () => {
      setIsAddCardOpen(false);
      setNewCardName('');
      queryClient.invalidateQueries({ queryKey: ['profileSubscription'] });
      queryClient.invalidateQueries({ queryKey: ['creditCards'] });
    },
    setStripeError,
    identityService,
  });

  const updateUserMutation = useUpdateUserMutation({
    userSub: context?.user?.sub?.toString() ?? '',
    currentUsername: context?.user?.username ?? '',
    onSuccessCallback: () => {
      setIsOpenChangeUserNameModal(false);
      queryClient.invalidateQueries({ queryKey: ['profileSubscription'] });
      refreshUser && refreshUser();
    },
    identityService,
  });

  const changeBillingAddressMutation = useChangeBillingAddressMutation({
    billingInfoId: profileSubscriptionData?.billing?.id,
    onSuccessCallback: () => {
      setIsChangeAddressModalOpen(false);
      // queryClient.invalidateQueries({ queryKey: ['profileSubscription'] }) // Already handled in hook
    },
    identityService,
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: () => {
      if (!identityService) {
        throw new Error(
          'Identity service is not available to cancel subscription.',
        );
      }
      return identityService.selfserveAPI.subscriptionsControllerCancelSubscription();
    },
    onSuccess: () => {
      enqueueSnackbar('Subscription cancelled successfully', {
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['profileSubscription'] });
      setIsConfirmModalOpen(false);
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to cancel subscription: ${error.message}`, {
        variant: 'error',
      });
    },
  });

  const reactivateSubscriptionMutation = useMutation({
    mutationFn: () => {
      if (!identityService) {
        throw new Error(
          'Identity service is not available to reactivate subscription.',
        );
      }
      return identityService.selfserveAPI.subscriptionsControllerReactivateSubscription();
    },
    onSuccess: () => {
      enqueueSnackbar('Subscription reactivated successfully', {
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['profileSubscription'] });
      setIsConfirmModalOpen(false);
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to reactivate subscription: ${error.message}`, {
        variant: 'error',
      });
    },
  });

  const submitNewUserCredential = async (name: string) => {
    const payload: { fullName: string; email?: string } = { fullName: name };
    if (context?.user?.username) {
      payload.email = context?.user?.username;
    }
    updateUserMutation.mutate(payload);
  };

  const accountInfo = {
    name: profileSubscriptionData?.account?.name ?? '-',
    email: profileSubscriptionData?.account?.email ?? '-',
    company: profileSubscriptionData?.account?.company ?? '-',
  };

  const currentBillingAddress = {
    address: profileSubscriptionData?.billing?.address ?? '-',
    city: profileSubscriptionData?.billing?.city ?? '-',
    state: profileSubscriptionData?.billing?.state ?? '-',
  };

  const billingAddress = {
    address: profileSubscriptionData?.billing
      ? `${profileSubscriptionData.billing.address}`
      : 'N/A',
  };

  const activeCard = cards ? cards?.find((card) => card.isDefault) : undefined;

  const planDetails = {
    planName: profileSubscriptionData?.plan?.name || 'No plan',
    regionActive: profileSubscriptionData?.plan?.region || 'No region',
    status: profileSubscriptionData?.plan?.status || 'No status',
    access: 'Full access',
    badgeText: profileSubscriptionData?.plan?.name || 'No plan',
  };

  const upcomingPayment = {
    amount: profileSubscriptionData?.plan?.nextPaymentAmount
      ? `${(profileSubscriptionData.plan.nextPaymentAmount / 100).toFixed(2)} ${
          profileSubscriptionData.plan.currency
        }`
      : '-', // TODO: fix currency
    date: profileSubscriptionData?.plan?.nextBillingDate
      ? formatDate(new Date(profileSubscriptionData.plan.nextBillingDate))
      : '',
  };

  const paymentDetailsData = {
    billingCycle: profileSubscriptionData?.payment?.billingType
      ? `${profileSubscriptionData.payment.billingType} - ${(
          (profileSubscriptionData.plan?.nextPaymentAmount ?? 0) / 100
        ).toFixed(2)} ${profileSubscriptionData.plan?.currency}`
      : '-',

    paymentMethodCard: activeCard
      ? `**** **** **** ${activeCard.lastFourDigits}`
      : profileSubscriptionData?.payment?.paymentMethod?.card ||
        'No default card',
    expiryDate: 'N/A', // TODO: Fetch this data, CreditCardDto does not have expiryMonth/Year. Profile sub data also might not have it directly.
  };

  // Timer useEffect
  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;
    if (isConfirmModalOpen && timer > 0) {
      setIsConfirmButtonEnabled(false);
      intervalId = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (isConfirmModalOpen && timer === 0) {
      setIsConfirmButtonEnabled(true);
      if (intervalId) clearInterval(intervalId);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isConfirmModalOpen, timer]);

  const handleOpenConfirmationModal = (
    actionType: 'cancel' | 'reactivate',
    actionFn: () => void,
    title: string,
    description: string,
  ) => {
    setCurrentActionType(actionType);
    setConfirmModalAction(() => actionFn);
    setConfirmModalTitle(title);
    setConfirmModalBaseDescription(description);
    setTimer(10);
    setIsConfirmButtonEnabled(false);
    setIsConfirmModalOpen(true);
  };

  const getPlanActionDetails = () => {
    if (planDetails.status === PlanInformationStatusEnum.Active) {
      return {
        text: cancelSubscriptionMutation.isPending
          ? 'Cancelling...'
          : 'Cancel subscription',
        action: () =>
          handleOpenConfirmationModal(
            'cancel',
            () => cancelSubscriptionMutation.mutate(),
            'Confirm Cancellation',
            'Are you sure you want to cancel your subscription? This action will take effect at the end of your current billing period.',
          ),
        isLoading: cancelSubscriptionMutation.isPending,
      };
    }
    if (planDetails.status === PlanInformationStatusEnum.Cancelled) {
      return {
        text: reactivateSubscriptionMutation.isPending
          ? 'Reactivating...'
          : 'Reactivate subscription',
        action: () => reactivateSubscriptionMutation.mutate(),
        isLoading: reactivateSubscriptionMutation.isPending,
      };
    }
    return { text: '', action: undefined, isLoading: false };
  };

  const planActionDetails = getPlanActionDetails();

  if (!context?.user?.sub || !identityService) {
    return <LoadingScreen text="Loading profile..." />;
  }

  return (
    <div className="container mx-auto py-6 px-4 sm:px-6 lg:px-8">
      {/* {!profileSubscriptionData?.plan && !isLoadingProfileSubscription && (
        <>
          <div className="mb-6">
            <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-red-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-red-800 mb-2">
                    Your plan looks like it ended
                  </h3>
                  <p className="text-red-700 mb-4">
                    It seems your current subscription has expired or been
                    cancelled. To continue accessing premium features and
                    analytics, please browse our available plans and reactivate
                    your subscription.
                  </p>
                  <Button
                    onClick={() => {
                      window.location.href = '/pricing';
                    }}
                    className="bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 text-white px-6 py-2"
                  >
                    Browse Plans
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )} */}

      <div>
        {context?.user?.products.length === 0 &&
          profileSubscriptionData?.plan === null && (
            <>
              <div className="z-10 absolute top-0 left-0 h-full w-full bg-white-0 rounded-md bg-clip-padding backdrop-filter backdrop-blur-md bg-opacity-10 border border-gray-100">
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="max-w-md mx-auto text-center space-y-6">
                    {/* GrowIntel Logo */}
                    <div className="flex justify-center mb-4">
                      <img
                        src={GrowIntelLogo}
                        alt="GrowIntel Logo"
                        className="w-[86%]"
                      />
                    </div>

                    {/* Main Text */}
                    <div className="space-y-3">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Welcome to GrowIntel
                      </h3>
                      <p className="text-gray-600 leading-relaxed dark:text-white">
                        Unlock powerful analytics and insights to grow your
                        business. Browse our subscription plans to access
                        premium features and advanced tools designed for your
                        success.
                      </p>
                    </div>

                    {/* Call to Action */}
                    <div className="pt-4">
                      <Button
                        onClick={() => {
                          window.location.href = '/pricing';
                        }}
                        className="bg-gradient-to-r from-[#02006D] to-[#5BE0DC] hover:from-[#100786] hover:to-[#46d2c6] text-white px-6 py-2"
                      >
                        Access
                      </Button>
                    </div>

                    {/* Test Content */}
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
                      <div className="text-xs text-gray-400 space-y-1 dark:text-white">
                        <p>• Profile: {context?.user?.name || 'Test User'}</p>
                        <p>• Status: No Active Subscription</p>
                        <p>• Mode: Development</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
        <div className="lg:col-span-3 space-y-8">
          <InfoCard
            title="Account info"
            headerInfo=""
            elements={[
              { title: 'Name', value: accountInfo.name ?? '' },
              { title: 'Email', value: accountInfo.email ?? '' },
              { title: 'Company', value: accountInfo.company ?? '' },
            ]}
            buttonText={
              profileSubscriptionData?.plan ? 'Edit information' : undefined
            }
            onButtonClįck={() => {
              profileSubscriptionData?.plan
                ? setIsOpenChangeUserNameModal(true)
                : undefined;
            }}
          />
          <InfoCard
            title="Billing address"
            headerInfo=""
            elements={[{ title: 'Address', value: billingAddress.address }]}
            buttonText={
              profileSubscriptionData?.plan ? 'Change address' : undefined
            }
            onButtonClįck={() => {
              profileSubscriptionData
                ? setIsChangeAddressModalOpen(true)
                : undefined;
            }}
          />
        </div>
        <div className="lg:col-span-3 space-y-8">
          <InfoCard
            title="Your plan"
            headerInfo={planDetails.badgeText}
            elements={[
              {
                title: 'Plan',
                value: planDetails.planName,
              },
              {
                title: 'Region Active',
                value: `${planDetails.regionActive}`,
              },
              {
                title: 'Status',
                value: `${planDetails.status}`,
                color:
                  planDetails.status === PlanInformationStatusEnum.Active
                    ? 'text-green-500'
                    : planDetails.status === PlanInformationStatusEnum.Cancelled
                      ? 'text-red-500'
                      : 'text-gray-500',
              },
              {
                title: 'Access',
                value: planDetails.access,
              },
            ]}
            insideCard={
              profileSubscriptionData?.plan
                ? {
                    icon: (
                      <DollarSign
                        width={20}
                        height={20}
                        className="text-[#78BEE9]"
                      />
                    ),
                    title: upcomingPayment.amount,
                    value: 'Next payment',
                    date: upcomingPayment.date,
                  }
                : undefined
            }
            // buttonText='Change plan'
            // onButtonClįck={() => {
            //   if (!activeCard) {
            //     enqueueSnackbar(
            //       'Please set a default payment method before changing your plan.',
            //       { variant: 'warning' }
            //     );
            //     return;
            //   }
            //   if (tiers.length === 0) {
            //     enqueueSnackbar(
            //       'Plan information is not available at the moment. Please try again later.',
            //       { variant: 'info' }
            //     );
            //     return;
            //   }
            //   setIsChangePlanDialogOpen(true);
            // }}
            secondButtonText={planActionDetails.text}
            onSecondButtonClįck={planActionDetails.action}
            secondButtonLoading={planActionDetails.isLoading}
            secondButtonType={
              planActionDetails.text.toLowerCase().includes('cancel')
                ? 'destructive'
                : 'default'
            }
          />
          <InfoCard
            title="Payment Details"
            headerInfo=""
            elements={[
              {
                title: 'Billing',
                value: paymentDetailsData.billingCycle,
              },
              { title: '', value: '' },
              {
                title: 'Payment method',
                value: paymentDetailsData.paymentMethodCard,
              },
            ]}
            buttonText={
              profileSubscriptionData?.plan
                ? 'Change payment method'
                : undefined
            }
            onButtonClįck={() => {
              profileSubscriptionData ? setIsManageCardsOpen(true) : undefined;
            }}
          />
        </div>

        <div className="lg:col-span-6 space-y-8">
          <InvoicesList
            currency={profileSubscriptionData?.plan?.currency ?? ''}
            invoices={
              profileSubscriptionData?.invoices?.map((invoice) => ({
                date: invoice.date,
                amount: invoice.amount,
                status: invoice.status as 'paid' | 'cancel' | 'refund',
              })) ?? []
            }
          />
        </div>
      </div>
      <ChangeUserProfile
        isOpen={isOpenChangeUserNameModal}
        setIsOpen={setIsOpenChangeUserNameModal}
        handleSubmit={submitNewUserCredential}
        userName={context?.user?.name ?? ''}
        email={context?.user?.username ?? ''}
      />

      <AddPaymentMethodDialog
        isOpen={isAddCardOpen}
        setIsOpen={setIsAddCardOpen}
        onAddCard={async () => {
          addCardMutation.mutate();
        }}
        isSubmitting={addCardMutation.isPending}
        stripe={stripe}
        elements={elements}
        newCardName={newCardName}
        setNewCardName={setNewCardName}
        stripeError={stripeError}
        setStripeError={setStripeError}
        cardComplete={cardComplete}
        setCardComplete={setCardComplete}
        cardElementOptions={CARD_ELEMENT_OPTIONS}
      />
      <ManagePaymentMethodsDialog
        isOpen={isManageCardsOpen}
        setIsOpen={setIsManageCardsOpen}
        cards={cards}
        onSetDefault={async (id) => {
          setDefaultCardMutation.mutate(id);
        }}
        onRemove={async (id) => {
          removeCardMutation.mutate(id);
        }}
        onAddCard={() => {
          setIsManageCardsOpen(false);
          setIsAddCardOpen(true);
        }}
        isLoading={
          isLoadingCards ||
          isLoadingProfileSubscription ||
          setDefaultCardMutation.isPending ||
          removeCardMutation.isPending ||
          cancelSubscriptionMutation.isPending ||
          reactivateSubscriptionMutation.isPending
        }
      />

      <ChangeBillingAddressDialog
        isOpen={isChangeAddressModalOpen}
        setIsOpen={setIsChangeAddressModalOpen}
        handleSubmit={(data) => {
          changeBillingAddressMutation.mutate({ address: data.address });
        }}
        isSubmitting={changeBillingAddressMutation.isPending}
        initialAddress={currentBillingAddress.address}
        initialCity={currentBillingAddress.city}
        initialState={currentBillingAddress.state}
      />

      {isConfirmModalOpen && (
        <ConfirmDialog
          open={isConfirmModalOpen}
          onClose={() => {
            setIsConfirmModalOpen(false);
            setTimer(10);
            setIsConfirmButtonEnabled(false);
          }}
          title={confirmModalTitle}
          content={
            <>
              <p>{confirmModalBaseDescription}</p>
              {timer > 0 && (
                <p className="mt-2 text-sm text-gray-500">
                  Please wait {timer} seconds to confirm.
                </p>
              )}
              {timer === 0 && (
                <p className="mt-2 text-sm text-gray-600">
                  You can now confirm the action.
                </p>
              )}
            </>
          }
          action={
            <Button
              onClick={() => {
                if (confirmModalAction) {
                  confirmModalAction();
                }
              }}
              disabled={
                !isConfirmButtonEnabled ||
                (currentActionType === 'cancel' &&
                  cancelSubscriptionMutation.isPending) ||
                (currentActionType === 'reactivate' &&
                  reactivateSubscriptionMutation.isPending)
              }
              variant={
                (currentActionType === 'cancel' &&
                  cancelSubscriptionMutation.isPending) ||
                (currentActionType === 'reactivate' &&
                  reactivateSubscriptionMutation.isPending)
                  ? 'outline'
                  : 'default'
              }
              color={
                confirmModalTitle.toLowerCase().includes('cancel')
                  ? 'error'
                  : 'primary'
              }
            >
              {(currentActionType === 'cancel' &&
                cancelSubscriptionMutation.isPending) ||
              (currentActionType === 'reactivate' &&
                reactivateSubscriptionMutation.isPending)
                ? 'Processing...'
                : currentActionType === 'cancel'
                  ? timer > 0
                    ? 'Confirm Cancellation'
                    : 'Confirm Cancellation'
                  : timer > 0
                    ? 'Cancel'
                    : 'Cancel'}
            </Button>
          }
        />
      )}
    </div>
  );
};

const ProfileView = () => {
  return (
    <Elements stripe={stripePromise}>
      <ProfileViewContent />
    </Elements>
  );
};

export default ProfileView;
